import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, ArrowRight, Building2, Truck, ClipboardList, Compass } from "lucide-react";
import { api } from "@/lib/api";

const MODULES = [
    { to: "/dashboard", label: "Dashboard Ejecutivo", keywords: "kpi indicadores resumen" },
    { to: "/dispatch", label: "Despacho Operativo", keywords: "kanban cabina" },
    { to: "/map", label: "Mapa en Vivo", keywords: "mapa unidades gps" },
    { to: "/services", label: "Servicios", keywords: "lista servicios" },
    { to: "/services/new", label: "Nueva Solicitud", keywords: "crear servicio" },
    { to: "/quoter", label: "Cotizador", keywords: "cotizar precio quote" },
    { to: "/quotes", label: "Cotizaciones", keywords: "pipeline cotizaciones" },
    { to: "/clients", label: "Clientes & Convenios", keywords: "crm corporativo" },
    { to: "/agreements", label: "Tarifario por Convenio", keywords: "convenios tarifas" },
    { to: "/price-lists", label: "Listas de Precios", keywords: "precios tarifario" },
    { to: "/units", label: "Unidades", keywords: "ambulancias flota" },
    { to: "/personnel", label: "Personal", keywords: "paramedicos operadores" },
    { to: "/payroll", label: "Nómina del periodo", keywords: "pago nomina sueldos honorarios eventuales horas guardias" },
    { to: "/maintenance", label: "Mantenimiento", keywords: "checklist preventivo correctivo taller" },
    { to: "/inventory", label: "Inventario y Almacenes", keywords: "stock botiquin consumibles" },
    { to: "/receivable", label: "Cuentas por Cobrar", keywords: "facturas pagos cobro" },
    { to: "/payable", label: "Cuentas por Pagar", keywords: "proveedores ap" },
    { to: "/aging", label: "Antigüedad de Saldos", keywords: "aging saldos" },
    { to: "/sla", label: "Cumplimiento SLA", keywords: "tiempos respuesta" },
    { to: "/expirations", label: "Centro de alertas", keywords: "vencimientos" },
    { to: "/incidents", label: "Incidentes", keywords: "siniestros percances" },
    { to: "/reports", label: "Reportes", keywords: "metricas" },
    { to: "/audit", label: "Auditoría", keywords: "log diff" },
    { to: "/attendance", label: "Asistencia y Turnos", keywords: "checkin nomina" },
    { to: "/integrations", label: "Integraciones", keywords: "twilio resend ecofactura claves" },
    { to: "/hospital-contacts", label: "Contactos Hospitales", keywords: "hospitales" },
    { to: "/portal-users", label: "Usuarios Portal", keywords: "clientes acceso" },
    { to: "/settings", label: "Configuración", keywords: "demo seed contrasena" },
];

export default function GlobalSearch({ onClose }) {
    const navigate = useNavigate();
    const [q, setQ] = useState("");
    const [results, setResults] = useState({ services: [], clients: [], units: [] });
    const [activeIdx, setActiveIdx] = useState(0);
    const inputRef = useRef(null);

    useEffect(() => { inputRef.current?.focus(); }, []);
    // ESC para cerrar
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    // Buscar en módulos + servicios + clientes + unidades
    useEffect(() => {
        if (!q || q.length < 2) { setResults({ services: [], clients: [], units: [] }); return; }
        let cancelled = false;
        const t = setTimeout(async () => {
            try {
                const [s, c, u] = await Promise.all([
                    api.get("/services", { params: { limit: 200 } }).catch(() => ({ data: [] })),
                    api.get("/clients").catch(() => ({ data: [] })),
                    api.get("/units").catch(() => ({ data: [] })),
                ]);
                if (cancelled) return;
                const ql = q.toLowerCase();
                setResults({
                    services: (s.data || []).filter(x =>
                        (x.folio || "").toLowerCase().includes(ql) ||
                        (x.patient_name || "").toLowerCase().includes(ql)).slice(0, 5),
                    clients: (c.data || []).filter(x =>
                        (x.business_name || "").toLowerCase().includes(ql) ||
                        (x.rfc || "").toLowerCase().includes(ql)).slice(0, 5),
                    units: (u.data || []).filter(x =>
                        (x.economic_number || "").toLowerCase().includes(ql) ||
                        (x.plates || "").toLowerCase().includes(ql)).slice(0, 5),
                });
            } catch (e) { /* silent */ void e; }
        }, 200);
        return () => { cancelled = true; clearTimeout(t); };
    }, [q]);

    const modules = useMemo(() => {
        if (!q || q.length < 1) return MODULES.slice(0, 8);
        const ql = q.toLowerCase();
        return MODULES.filter(m => m.label.toLowerCase().includes(ql) || (m.keywords || "").includes(ql)).slice(0, 8);
    }, [q]);

    const flatItems = useMemo(() => {
        const list = [];
        modules.forEach(m => list.push({ kind: "module", to: m.to, label: m.label }));
        results.services.forEach(s => list.push({ kind: "service", to: `/services/${s.id}`, label: `${s.folio} · ${s.patient_name}`, hint: s.status }));
        results.clients.forEach(c => list.push({ kind: "client", to: `/statement/${c.id}`, label: c.business_name, hint: c.rfc || "Cliente" }));
        results.units.forEach(u => list.push({ kind: "unit", to: "/units", label: `${u.economic_number} · ${u.plates}`, hint: u.status }));
        return list;
    }, [modules, results]);

    const go = (item) => { navigate(item.to); onClose(); };

    const onKeyDown = (e) => {
        if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, flatItems.length - 1)); }
        if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
        if (e.key === "Enter" && flatItems[activeIdx]) { e.preventDefault(); go(flatItems[activeIdx]); }
    };

    const renderGroup = (title, items, icon) => {
        if (items.length === 0) return null;
        const Icon = icon;
        return (
            <div className="mb-2" key={title}>
                <div className="px-4 py-1 text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1">
                    {Icon && <Icon className="w-3 h-3" />} {title}
                </div>
                {items.map((it) => {
                    const idx = flatItems.findIndex(x => x.to === it.to && x.label === it.label);
                    const active = idx === activeIdx;
                    return (
                        <button
                            key={`${it.to}-${it.label}`}
                            onClick={() => go(it)}
                            onMouseEnter={() => setActiveIdx(idx)}
                            className={`w-full text-left px-4 py-2 flex items-center justify-between gap-3 ${active ? "bg-red-50 text-red-900" : "hover:bg-slate-50"}`}
                            data-testid={`search-result-${it.to.replace(/\//g, "-")}`}
                        >
                            <div className="flex-1 min-w-0">
                                <div className="text-sm truncate">{it.label}</div>
                                {it.hint && <div className="text-[10px] text-slate-500 font-mono">{it.hint}</div>}
                            </div>
                            <ArrowRight className={`w-3 h-3 ${active ? "text-red-600" : "text-slate-300"}`} />
                        </button>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm flex items-start justify-center pt-20 px-4" onClick={onClose} data-testid="global-search-overlay">
            <div className="bg-white rounded-sm w-full max-w-2xl max-h-[70vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200">
                    <Search className="w-4 h-4 text-slate-400" />
                    <input
                        ref={inputRef}
                        value={q}
                        onChange={(e) => { setQ(e.target.value); setActiveIdx(0); }}
                        onKeyDown={onKeyDown}
                        placeholder="Buscar folio, cliente, unidad, módulo… (↑↓ navega, Enter abre, Esc cierra)"
                        className="flex-1 outline-none text-sm placeholder:text-slate-400"
                        data-testid="global-search-input"
                    />
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto py-2">
                    {flatItems.length === 0 && (
                        <div className="px-4 py-8 text-center text-sm text-slate-400">
                            <Compass className="w-8 h-8 mx-auto mb-2 text-slate-300" strokeWidth={1} />
                            Empieza a escribir para navegar — folio, RFC, paciente, número económico de ambulancia o nombre del módulo.
                        </div>
                    )}
                    {renderGroup("Módulos", modules.map(m => ({ to: m.to, label: m.label })), Compass)}
                    {renderGroup("Servicios", results.services.map(s => ({ to: `/services/${s.id}`, label: `${s.folio} · ${s.patient_name}`, hint: s.status })), ClipboardList)}
                    {renderGroup("Clientes", results.clients.map(c => ({ to: `/statement/${c.id}`, label: c.business_name, hint: c.rfc || "Cliente" })), Building2)}
                    {renderGroup("Unidades", results.units.map(u => ({ to: "/units", label: `${u.economic_number} · ${u.plates}`, hint: u.status })), Truck)}
                </div>
                <div className="border-t border-slate-200 px-4 py-2 text-[10px] text-slate-500 font-mono flex items-center justify-between">
                    <span>↑↓ navega · Enter abre · Esc cierra</span>
                    <span>⌘K · Ctrl+K</span>
                </div>
            </div>
        </div>
    );
}
